// static/table_engine_src/engine.js

import { ensureSection, escCss } from "./util/dom.js";
import { num, clampInt } from "./util/number.js";

import { installFormat } from "./format/install.js";
import { installAutosize } from "./layout/autosize.js";
import { installColumnLayout } from "./layout/column_layout.js";

import { installClipboardOps } from "./core/ops_clipboard.js";
import { installSelection } from "./core/selection.js";
import { installRender } from "./core/render.js";
import { installDpOverrides } from "./core/dp_overrides.js";
import { installMenu } from "./core/menu.js";
import { installCommands } from "./core/commands.js";
import { installMenuBuilders } from "./core/menu_builders.js";
import { installTotals } from "./totals.js";

import { normalizeSpec } from "./core/spec_normalize.js";
import { getActiveEngine, installGlobalEscOnce, setActiveEngine } from "./core/esc_global.js";
import { installLifecycle } from "./core/lifecycle.js";
import { installColumnOps } from "./core/ops_columns.js";
import { installRowOps } from "./core/ops_rows.js";
import { installWiring } from "./core/wiring.js";
import { installValidation } from "./core/validation.js";

// ---------------------------------------------------------
// Shared numeric conversion helpers
// ---------------------------------------------------------
function displayFromCanonical(x, fmt) {
  const n = Number(x);
  if (!Number.isFinite(n)) return NaN;
  return n * (fmt?.scale ?? 1);
}

function canonicalFromDisplay(x, fmt) {
  const n = Number(x);
  if (!Number.isFinite(n)) return NaN;
  return n / (fmt?.scale ?? 1);
}

// ---------------------------------------------------------
// engine factory
// ---------------------------------------------------------
function create(spec, opts = {}) {
  if (!spec || typeof spec !== "object") return null;

  const root = spec.rootId ? document.getElementById(spec.rootId) : null;
  const table = spec.tableId ? document.getElementById(spec.tableId) : null;
  if (!root || !table) return null;

  const prevEngine = table.__te_engine;
  if (prevEngine && typeof prevEngine.destroy === "function") {
    try {
      prevEngine.destroy();
    } catch {}
  }

  const thead = ensureSection(table, "thead");
  const tbody = ensureSection(table, "tbody");
  ensureSection(table, "tfoot"); // do not overwrite

  const onEdit = typeof opts.onEdit === "function" ? opts.onEdit : null;
  const onAction = typeof opts.onAction === "function" ? opts.onAction : null;
  const onRender = typeof opts.onRender === "function" ? opts.onRender : null;
  const onValidationChange =
    typeof opts.onValidationChange === "function" ? opts.onValidationChange : null;

  const notify = opts.notify || window.notify || {
    ok: (m) => console.log(m),
    err: (m) => console.warn(m),
    info: (m) => console.log(m),
  };

  const actionHandlers =
    opts.actionHandlers && typeof opts.actionHandlers === "object" ? opts.actionHandlers : {};

  // --- normalize spec/columns (big win)
  const norm = normalizeSpec(spec);
  if (!norm) return null;

  const {
    cols,
    colByKey,
    hiddenCols,
    rowMenuEnabled,
    rowMenuHoverEnabled,
    ROW_HANDLE_KEY,
    spreadsheetEnabled,
    autoWidthMode,
    sizeMode,
    autoWidthKeys,
    colIndexByKey,
  } = norm;

  // apply sizing mode CSS hook
  table.classList.remove("te-auto", "te-fill");
  table.classList.add(sizeMode === "fill" ? "te-fill" : "te-auto");

  const cleanups = [];
  function addCleanup(fn) {
    if (typeof fn !== "function") return () => {};
    let done = false;
    const wrapped = () => {
      if (done) return;
      done = true;
      try {
        fn();
      } catch {}
    };
    cleanups.push(wrapped);
    return wrapped;
  }

  function on(target, type, handler, options) {
    if (!target || typeof target.addEventListener !== "function") return handler;
    if (typeof handler !== "function") return handler;
    target.addEventListener(type, handler, options);
    addCleanup(() => {
      try {
        target.removeEventListener(type, handler, options);
      } catch {}
    });
    return handler;
  }

  // build ctx that modules can attach to
  const ctx = {
    spec,
    root,
    table,
    thead,
    tbody,
    cols,
    colByKey,
    hiddenCols,
    escCss,

    rowMenuEnabled,
    rowMenuHoverEnabled,
    ROW_HANDLE_KEY,

    autoWidthMode,
    autoWidthKeys: () => autoWidthKeys(),

    colIndexByKey: (k) => colIndexByKey(k),
    notify,
    onEdit,
    onValidationChange,
    addCleanup,
    on,
  };

  installColumnLayout(ctx);

  // install extracted modules
  installFormat(ctx, { installWindowExports: opts.installWindowExports !== false });
  installDpOverrides(ctx);
  installAutosize(ctx);

  // lifecycle before render (render.js expects scheduleNotifyRendered, setSuppressRenderNotify)
  installLifecycle(ctx, { onRender });

  installRender(ctx);
  installValidation(ctx);
  installTotals(ctx);

  // clipboard before selection/menu
  installClipboardOps(ctx);

  installSelection(ctx);
  installMenu(ctx);

  // ops (now off engine.js)
  installColumnOps(ctx);
  installRowOps(ctx, { spec, onEdit, onAction });

  // wiring glue
  installWiring(ctx, { onEdit, onAction, actionHandlers });

  // commands dispatcher (thin)
  installCommands(ctx);

  // global esc
  const releaseGlobalEsc = installGlobalEscOnce?.();
  addCleanup(() => {
    try {
      releaseGlobalEsc?.();
    } catch {}
  });

  function hasAnything() {
    const s = ctx._sel || {};
    const sel =
      (s.selectedCols && s.selectedCols.size) ||
      (s.selectedRows && s.selectedRows.size) ||
      !!s.cellFocus ||
      !!s.cellAnchor ||
      !!s.cellRange ||
      (Array.isArray(s.cellRanges) && s.cellRanges.length > 0);

    const menuOpen = !!ctx.isMenuOpen?.();
    return sel || menuOpen;
  }

  function clearEsc() {
    ctx.clearSelectionState?.();
    ctx.hideMenu?.();
  }

  const escBridge = {
    table,
    clearEsc,
    hasAnything,
    isMenuOpen: () => !!ctx.isMenuOpen?.(),
    hideMenu: () => {
      try {
        ctx.hideMenu?.();
      } catch {}
    },
  };

  function markActive() {
    try { document.body.classList.remove("te-inactive"); } catch {}
    setActiveEngine(escBridge);
  }

  on(table, "pointerdown", markActive, true);
  on(table, "focusin", markActive, true);
  on(thead, "pointerdown", markActive, true);
  on(thead, "contextmenu", markActive, true);
  on(tbody, "pointerdown", markActive, true);

  // ---------------------------------------------------------
  // Menu builders (still here; next step is to move this too if you want)
  // ---------------------------------------------------------
  function selectedNumericColKeys() {
    const selKeys = ctx._sel?.selectedCols?.size ? [...ctx._sel.selectedCols] : [];
    return selKeys.filter((k) => {
      const c = colByKey.get(k);
      if (!c) return false;
      if (!ctx.isNumericCol?.(c)) return false;

      const fmt = ctx.normalizeFormat?.(c);
      if (fmt?.int) return false;
      if (!fmt?.userDp) return false;

      const colFmt = c && typeof c === "object" ? c.format : null;
      const lockDp = !!(colFmt && typeof colFmt === "object" && colFmt.lockDp === true);
      if (lockDp) return false;

      return true;
    });
  }

  function pushHistoryAction(action) {
    if (!action || typeof action !== "object") return;
    if (typeof ctx.pushHistoryAction !== "function") return;
    ctx.pushHistoryAction({
      ...action,
      ts: Number.isFinite(action.ts) ? action.ts : Date.now(),
    });
  }

  function applyDpEntries(entries, dir = "redo") {
    for (const entry of entries || []) {
      if (!entry || !entry.colKey) continue;
      const next = dir === "undo" ? entry.before : entry.after;
      ctx.setDpOverride?.(entry.colKey, next);
    }

    ctx.renderHeader?.();
    ctx.reformatAllNumeric?.();
    ctx.updateTotals?.();
    ctx.scheduleNotifyRendered?.();
  }

  function applyDpToColumns(keys, dpOrNull, { notifyUser = true, recordHistory = true } = {}) {
    const uniqKeys = [...new Set((keys || []).filter((k) => !!k))];
    if (!uniqKeys.length) return 0;

    const nextDp = dpOrNull == null ? null : clampInt(dpOrNull, 0, 12);
    const entries = [];

    uniqKeys.forEach((k) => {
      const before = ctx.getDpOverride?.(k);
      const after = nextDp;
      if (before === after) return;

      ctx.setDpOverride?.(k, after);
      entries.push({
        colKey: k,
        before: before == null ? null : before,
        after,
      });
    });

    if (!entries.length) return 0;

    ctx.renderHeader?.();
    ctx.reformatAllNumeric?.();
    ctx.updateTotals?.();
    ctx.scheduleNotifyRendered?.();

    if (recordHistory) {
      const snapshot = entries.map((e) => ({ ...e }));
      pushHistoryAction({
        type: "decimals-override",
        label: nextDp == null ? "decimals-reset" : "decimals-set",
        undo: () => applyDpEntries(snapshot, "undo"),
        redo: () => applyDpEntries(snapshot, "redo"),
      });
    }

    if (notifyUser) {
      notify?.ok?.(
        nextDp == null
          ? `Decimals reset for ${entries.length} column(s).`
          : `Decimals set to ${nextDp} for ${entries.length} column(s).`,
      );
    }
    return entries.length;
  }

  installMenuBuilders(ctx, {
    spec,
    rowMenuEnabled,
    ROW_HANDLE_KEY,

    cols,
    colByKey,

    escCss,
    clampInt,
    isLockedCol: (c) => !!c?.locked,

    isNumericCol: ctx.isNumericCol,
    normalizeFormat: ctx.normalizeFormat,
    reformatAllNumeric: ctx.reformatAllNumeric,

    selectedNumericColKeys,
    applyDpToColumns,

    autosizeColumns: ctx.autosizeColumns,
    unhideAllColumns: ctx.unhideAllColumns,
    clearOutputsOnly: ctx.clearOutputsOnly,
    clearSelectedColumns: ctx.clearSelectedColumns,

    insertRowAbove: ctx.insertRowAbove,
    insertRowBelow: ctx.insertRowBelow,
    duplicateRows: ctx.duplicateRows,
    deleteRows: ctx.deleteRows,
    clearSelectedRowsInputs: ctx.clearSelectedRowsInputs,
    clearSelectedRowsOutputs: ctx.clearSelectedRowsOutputs,

    getRowEls: ctx.getRowEls,
    setSelectedColumns: ctx.setSelectedColumns,
    setSelectedRows: ctx.setSelectedRows,
    colIndexByKey: ctx.colIndexByKey,

    firstPasteableInputColIndex: ctx.firstPasteableInputColIndex,
    copyRowsToTSV: ctx.copyRowsToTSV,
    pasteIntoRows: ctx.pasteIntoRows,
    copyToClipboard: ctx.copyToClipboard,
    pasteFromClipboard: ctx.pasteFromClipboard,
    fillDownFromSelection: ctx.fillDownFromSelection,

    notify,
  });

  // ---------------------------------------------------------
  // mount
  // ---------------------------------------------------------
  let api = null;

  let _destroyed = false;
  function mount() {
    if (_destroyed) return;
    try {
      table.__te_engine = api;
    } catch {}

    table.classList.remove("te-auto", "te-fill");
    table.classList.add(sizeMode === "fill" ? "te-fill" : "te-auto");

    ctx.renderColgroup?.();
    ctx.renderHeader?.();

    ctx.wireFormatting?.();
    ctx.wireActions?.();

    try {
      table.tabIndex = 0;
    } catch {}

    if (spreadsheetEnabled) {
      ctx.wireSelectionGlue?.({ rowMenuEnabled, rowMenuHoverEnabled, ROW_HANDLE_KEY });

      // selection.js owns keyboard now
      ctx.wireKeyboard?.();

      ctx.applySelectionVisuals?.();
      ctx.updateRowHandles?.();
    } else {
      ctx.clearSelectionVisuals?.();
      ctx.updateRowHandles?.();
    }

    ctx.reformatAllNumeric?.();
    ctx.validateAll?.({ commit: true, emit: true });

    if (sizeMode !== "fill") ctx.scheduleAutoWidth?.();

    ctx.scheduleNotifyRendered?.();
  }

  // ---------------------------------------------------------
  // public API (same surface)
  // ---------------------------------------------------------
  api = {
    version: "1.4.9",
    root,
    table,
    spec,

    mount,
    destroy: () => {
      if (_destroyed) return;
      _destroyed = true;

      try {
        ctx.hideMenu?.();
      } catch {}

      try {
        if (document.__teActiveMenuCtx === ctx) document.__teActiveMenuCtx = null;
      } catch {}

      try {
        const m = ctx._menuEl;
        if (m && m.parentNode) m.parentNode.removeChild(m);
      } catch {}
      try {
        ctx._menuEl = null;
      } catch {}

      for (let i = cleanups.length - 1; i >= 0; i--) {
        try {
          cleanups[i]?.();
        } catch {}
      }
      cleanups.length = 0;

      try {
        if (table.__te_engine === api) delete table.__te_engine;
      } catch {
        try {
          if (table.__te_engine === api) table.__te_engine = null;
        } catch {}
      }

      try {
        if (getActiveEngine?.() === escBridge) setActiveEngine(null);
      } catch {}

      try {
        ctx._setLifecycleApi?.(null);
      } catch {}

      try {
        ctx.__api = null;
      } catch {}
    },

    // rows
    addRow: (item = {}, opts = {}) => ctx.renderRow?.(item, opts),
    addRows: (items = []) => (Array.isArray(items) ? items.map((it) => ctx.renderRow?.(it)) : []),
    renderRow: ctx.renderRow,
    clearRows: ctx.clearRows,
    getRowEls: ctx.getRowEls,
    deleteRows: ctx.deleteRows,

    // selectors
    getInput: ctx.getInput,
    getOut: ctx.getOut,

    // data
    readRow: ctx.readRow,
    readAll: ctx.readAll,
    getItems: ctx.getItems,
    setItems: ctx.setItems,
    load: ctx.load,

    // outputs / totals
    setOutput: ctx.setOutput,
    setOutputs: ctx.setOutputs,
    getOutputRaw: ctx.getOutputRaw,
    updateTotals: ctx.updateTotals,

    // column ops
    setColumnHidden: ctx.setColumnHidden,
    setColumnsHidden: ctx.setColumnsHidden,
    unhideAllColumns: ctx.unhideAllColumns,
    setColumnWidth: ctx.setColumnWidth,
    moveColumn: (colKey, targetKey, opts = {}) => ctx.moveColumnByKey?.(colKey, targetKey, opts),
    saveLayoutProfile: () => ctx.persistLayoutProfile?.(),
    clearLayoutProfile: () => ctx.clearLayoutProfile?.(),
    resetLayoutProfile: (opts = {}) => ctx.resetLayoutProfile?.(opts),
    autosizeColumns: ctx.autosizeColumns,
    refreshAutoWidth: ctx.autosizeColumns,

    // selection / clearing ops
    cmdUndo: (opts = {}) => ctx.cmdUndo?.(opts),
    cmdRedo: (opts = {}) => ctx.cmdRedo?.(opts),
    undo: (opts = {}) => ctx.cmdUndo?.(opts),
    redo: (opts = {}) => ctx.cmdRedo?.(opts),
    clearOutputsOnly: ctx.clearOutputsOnly,
    clearSelectedColumns: ctx.clearSelectedColumns,
    clearSelectedRowsInputs: ctx.clearSelectedRowsInputs,
    clearSelectedRowsOutputs: ctx.clearSelectedRowsOutputs,
    fillDownFromSelection: ctx.fillDownFromSelection,
    fillDownSelectionToRow: ctx.fillDownSelectionToRow,
    clearRange: ctx.clearRange,
    clearCell: ctx.clearCell,

    // decimals overrides
    setColumnDecimals: (colKey, dp) => {
      applyDpToColumns([colKey], dp, { notifyUser: false, recordHistory: true });
    },
    resetColumnDecimals: (colKey) => {
      applyDpToColumns([colKey], null, { notifyUser: false, recordHistory: true });
    },
    getColumnDecimals: (colKey) => ctx.getDpOverride?.(colKey),

    // formatting helpers
    reformatAllNumeric: ctx.reformatAllNumeric,

    // validation
    validateCell: ctx.validateCell,
    validateRow: ctx.validateRow,
    validateAll: ctx.validateAll,
    validateForCommit: ctx.validateForCommit,
    hasValidationErrors: ctx.hasValidationErrors,
    getValidationErrors: ctx.getValidationErrors,
    getFirstValidationError: ctx.getFirstValidationError,
    focusValidationError: ctx.focusValidationError,
    clearValidationErrors: ctx.clearValidationErrors,

    // row ops
    insertRowAbove: ctx.insertRowAbove,
    insertRowBelow: ctx.insertRowBelow,
    duplicateRows: ctx.duplicateRows,

    // wiring hooks
    wireActions: ctx.wireActions,
  };

  // allow lifecycle to fire te:render + onRender with the real api
  ctx._setLifecycleApi?.(api);

  // wiring needs access to api in actionHandlers callback
  ctx.__api = api;

  // stable .items / ._items
  try {
    Object.defineProperty(api, "items", {
      get: () => ctx.getItems?.(),
      set: (v) => api.setItems(v),
      configurable: true,
    });
    Object.defineProperty(api, "_items", {
      get: () => ctx.getItems?.(),
      set: (v) => api.setItems(v),
      configurable: true,
    });
  } catch {}

  mount();
  return api;
}

export { create };
